/**
 * Build spanning countertops for contiguous runs of counter-height
 * items (lower-cabinets + dishwashers).
 *
 * Each "run" is a maximal sequence of counter-height items on the same
 * wall whose wall-param ranges touch (no gap wider than `MAX_BRIDGED_GAP`).
 * One countertop is emitted per run, spanning from the leftmost item's
 * start to the rightmost item's end. Stoves, refrigerators, and wall
 * ovens break runs because their countertop coverage differs (stoves
 * sit at counter height too but eat the counter; fridges and wall
 * ovens are taller than the counter).
 *
 * Pure factory — no I/O.
 */

const {
  KITCHEN_CABINET_DIMENSIONS,
  KITCHEN_COUNTERTOP_THICKNESS,
  KITCHEN_COUNTERTOP_FRONT_OVERHANG,
} = require('./constants');
const {
  getWallAxes,
  outboardOffsetFromArm,
  wallParamToSurfacePoint,
} = require('./wallMath');
const { MIN_MODULE_IN } = require('../cabinet-runs/moduleSolver');

const EPS = 0.005; // 5 mm tolerance for float comparison (depth equality)
/** A peninsula's back-panel thickness — the depth every slab on one covers. */
const PENINSULA_BACK_PANEL = 1 * 0.0254;
/** How close a run end must be to a peninsula's wall line to be meeting it. */
const BACK_PANEL_EPS = 1.5 * 0.0254;

/**
 * The widest hole a single countertop still spans.
 *
 * `EPS` is a float tolerance, not a design rule, and using it for along-wall
 * contiguity made the counter stop dead at any real gap. The run pass leaves
 * such gaps routinely: a scanned appliance standing inside a doorway
 * reservation is left where it stands (the packer refuses to move measured
 * geometry), so the cabinetry beside it stops short and the appliance is
 * marooned. On structure E5DED26F's kitchen that was a 2.8" hole between the
 * last cabinet and the dishwasher — the dishwasher became a run of its own,
 * carried no cabinet parent, and was dropped below, leaving it bare.
 *
 * `MIN_MODULE_IN` is the line: nothing narrower than the engine's smallest
 * module can stand in the hole, so it is a filler void rather than a break
 * between two runs — the same meaning `MIN_CABINET_GAP` carries in kitchen
 * post-processing, and the same holes rule 9.7 / 10f close by growing a
 * neighbour. A counter spans 9" unsupported without complaint.
 */
const MAX_BRIDGED_GAP = MIN_MODULE_IN * 0.0254;

/**
 * @typedef {Object} CounterHeightItem
 * @property {string} wallId
 * @property {{start: number, end: number}} range  Wall-param meters from bottomLeft.
 * @property {string} type  'lower-cabinet', 'dishwasher', or 'break' (ends the
 *   run; no slab of its own).
 * @property {string} id    Object id (used as a parent reference for cabinets).
 */

/**
 * Sort items along the wall and merge into contiguous runs, bridging any
 * hole up to `MAX_BRIDGED_GAP`.
 * Returns array of `{ start, end, depth, parentIds }` where `parentIds`
 * only includes 'lower-cabinet' items (dishwashers don't structurally
 * support the countertop, they just fill a slot under it). A run also
 * BREAKS where the cabinet depth changes — a shallow (12") run abutting a
 * deep (24") run gets its own countertop instead of one wrong-depth slab
 * spanning both. Items without a `depth` default to the standard depth.
 */
function groupIntoRuns(items) {
  const sorted = [...items].sort((a, b) => a.range.start - b.range.start);
  const runs = [];

  for (const item of sorted) {
    // A `break` item ends the run it falls in: what follows starts a new slab
    // whatever the gap. It contributes no span and no parent — a tall bay
    // stands there (`cabinet-runs/fillRun.js`, emitBay). The bridging rule
    // below is for HOLES between cabinets; a tall bay is not a hole.
    if (item.type === 'break') {
      runs.push(null);
      continue;
    }
    const depth = item.depth ?? KITCHEN_CABINET_DIMENSIONS.depth;
    const last = runs[runs.length - 1];
    if (
      last &&
      item.range.start <= last.end + MAX_BRIDGED_GAP &&
      Math.abs(last.depth - depth) < EPS
    ) {
      last.end = Math.max(last.end, item.range.end);
      if (item.type === 'lower-cabinet') last.parentIds.push(item.id);
      // A scribe (`cabinet-runs/scribeCounter.js`) carries the slab on to the
      // wall past the run's last box: remember the wall it reaches.
      if (item.type === 'scribe') last.scribeParams.push(item.range.end);
    } else {
      runs.push({
        start: item.range.start,
        end: item.range.end,
        depth,
        parentIds: item.type === 'lower-cabinet' ? [item.id] : [],
        // …or, starting a run, the wall it reaches back to.
        scribeParams: item.type === 'scribe' ? [item.range.start] : [],
      });
    }
  }

  return runs.filter(Boolean);
}

/**
 * @param {Object} wall
 * @param {{start: number, end: number, depth?: number, parentIds: string[]}} run
 * @param {number} floorY
 * @param {Object} [dims] - Counter-stack overrides from the run's dimension
 *   profile (cabinet-runs/profiles.js): `cabinetHeight`, `thickness`,
 *   `overhang`, `label`. Defaults are the kitchen constants, so every
 *   caller that predates profiles is unchanged.
 * @returns {Object|null} Countertop object, or null on degenerate input.
 */
function buildCountertopForRun(wall, run, floorY, dims = {}) {
  const axes = getWallAxes(wall);
  if (!axes) return null;

  // Depth of the cabinets under this run (uniform — groupIntoRuns breaks
  // a run where the depth changes). A shallow run profiles a ~13" surface.
  const cabinetDepth = run.depth ?? KITCHEN_CABINET_DIMENSIONS.depth;
  const cabinetHeight = dims.cabinetHeight ?? KITCHEN_CABINET_DIMENSIONS.height;
  const thickness = dims.thickness ?? KITCHEN_COUNTERTOP_THICKNESS;
  const overhang = dims.overhang ?? KITCHEN_COUNTERTOP_FRONT_OVERHANG;
  // For peninsulas (virtual walls), an additional 1" of cover panel
  // sits on the OUTSIDE of the wall — the countertop extends to cover
  // it so the slab reads as a unified surface on top of the cabinets
  // and the back panel together.
  const BACK_PANEL_THICKNESS = wall.isVirtual ? 1 * 0.0254 : 0;

  // Countertop spans from `BACK_PANEL_THICKNESS` outside the wall (the
  // back face of the cover panel) to `cabinetDepth + overhang` inside
  // the wall (cabinet face + 1" front overhang). Center is shifted
  // forward by (cabinetDepth + overhang − BACK_PANEL_THICKNESS) / 2.
  const countertopDepth = cabinetDepth + overhang + BACK_PANEL_THICKNESS;
  const centerOffset = (cabinetDepth + overhang - BACK_PANEL_THICKNESS) / 2;

  const runLength = run.end - run.start;
  if (runLength <= 0) return null;

  const runMid = (run.start + run.end) / 2;

  const surfacePoint = {
    x: axes.start.x + runMid * axes.wallDir.x,
    z: axes.start.z + runMid * axes.wallDir.z,
  };

  const center = {
    x: surfacePoint.x + centerOffset * axes.wallNormal.x,
    y: floorY + cabinetHeight + thickness / 2,
    z: surfacePoint.z + centerOffset * axes.wallNormal.z,
  };

  const ry = axes.wallRotationY;
  const stamp = `${wall.id}_${Math.round(run.start * 1000)}`;

  return {
    id: `countertop_${stamp}`,
    type: 'countertop',
    label: dims.label || 'Kitchen Countertop',
    category: { countertop: true },
    center,
    dimensions: {
      x: runLength,
      y: thickness,
      z: countertopDepth,
    },
    rotation: { x: 0, y: ry, z: 0 },
    parents: run.parentIds.map((id) => ({ id, type: 'lower-cabinet' })),
    addedBy: 'kitchen-layout',
    lastModified: new Date().toISOString(),
  };
}

/**
 * How far `point` sits outboard of whichever peninsula arm meets the wall
 * there, or null when none does.
 */
function outboardOffsetAtPeninsula(point, axes, wall, walls) {
  for (const other of walls) {
    if (!other || other === wall || !other.isVirtual) continue;
    const offset = outboardOffsetFromArm(point, axes, other, BACK_PANEL_EPS);
    if (offset != null) return offset;
  }
  return null;
}

/**
 * Grow a run that dies into a PENINSULA so its slab reaches that peninsula's
 * OUTER face — the outboard side of the 1" back panel.
 *
 * A peninsula's back panel is its finished outboard face, and every slab on a
 * peninsula is built to cover it. At the corner where two runs meet, the slab
 * that crosses is sized on its OWN wall, so it reaches only the other arm's
 * wall LINE — leaving that arm's panel bare for the width of the crossing and
 * stepping 1" against its neighbour. Extending the run past the corner closes
 * it, whatever put the runs together there: an L cabinet, an appliance at the
 * corner, or a single-sided unit. (Handling only the L-cabinet corner left the
 * others to be found one fixture at a time.)
 *
 * The growth is the RESIDUAL to that outer face, not a flat panel thickness,
 * because how far the run already reaches depends on what it is built on. Two
 * arms joined at a shared corner are snapped to their wall lines' intersection,
 * so a crossing arm's run stops ON the line and owes the full 1". A REAL wall
 * is clipped to the kitchen section, which is the peninsula's outer face — so
 * unless `materializeCandidate` reserved the panel strip there, its run already
 * covers the panel and owes nothing. Adding a flat inch to both put a 1" tab of
 * slab past the finished face with no cabinet under it (found on FB5B0707's
 * kitchen).
 *
 * Real walls need no such growth of their own — they have no panel to cap, and
 * their own corner box is covered by the perpendicular run.
 */
function extendRunIntoAbuttingPeninsula(wall, run, walls) {
  const axes = getWallAxes(wall);
  if (!axes) return run;
  const pointAt = (t) => ({
    x: axes.start.x + t * axes.wallDir.x,
    z: axes.start.z + t * axes.wallDir.z,
  });
  // How much more this end owes the panel it dies into. Clamped to the panel:
  // a run stopping short of the arm's line is a hole the fill left, not a cap
  // this pass may close.
  const owed = (t) => {
    const offset = outboardOffsetAtPeninsula(pointAt(t), axes, wall, walls);
    if (offset == null) return 0;
    return Math.min(
      PENINSULA_BACK_PANEL,
      Math.max(0, PENINSULA_BACK_PANEL - offset)
    );
  };
  let { start, end } = run;
  // Where it may reach one. An ARM is synthesized to its own length, so it
  // meets another only at its ends, within the strip the other's panel may
  // have reserved there — a boundary anywhere else on it is an appliance
  // break. A REAL wall is another matter: the run engine builds on the whole
  // room wall, which runs on past the peninsula, so the corner sits mid-wall
  // and asking for a wall END there skipped every real-wall corner the engine
  // built — the 1" × 24" bare strip of panel beside the peninsula's knee
  // overhang (Stocks / Scan 1, 2026-10-21). On a real wall the arm-line test
  // in `owed` IS the corner test: a run end lies on an arm's line only where
  // that arm meets the wall.
  const reaches = (t, atStart) =>
    !wall.isVirtual ||
    (atStart
      ? t <= PENINSULA_BACK_PANEL + EPS
      : t >= axes.wallLen - PENINSULA_BACK_PANEL - EPS);
  if (reaches(run.start, true)) start -= owed(run.start);
  if (reaches(run.end, false)) end += owed(run.end);
  return start === run.start && end === run.end ? run : { ...run, start, end };
}

/**
 * @param {{
 *   walls: Object[],
 *   counterHeightItems: CounterHeightItem[],
 *   floorY: number,
 * }} args
 *   Each item may carry a `depth` (the depth of the cabinet beneath it);
 *   the run countertop matches it, and runs break where depth changes.
 *   Items without a depth use the standard 24" cabinet depth.
 *   `dims` (optional) is the counter-stack override bundle from the run's
 *   dimension profile — see `buildCountertopForRun`.
 * @returns {Object[]} New countertop objects (one per contiguous run).
 */
function buildCountertopRuns({ walls, counterHeightItems, floorY, dims }) {
  const wallById = new Map(walls.map((w) => [w.id, w]));
  const itemsByWall = new Map();
  for (const item of counterHeightItems) {
    if (!itemsByWall.has(item.wallId)) itemsByWall.set(item.wallId, []);
    itemsByWall.get(item.wallId).push(item);
  }

  const countertops = [];
  for (const [wallId, items] of itemsByWall.entries()) {
    const wall = wallById.get(wallId);
    if (!wall) continue;
    const runs = groupIntoRuns(items).map((r) =>
      extendRunIntoAbuttingPeninsula(wall, r, walls)
    );
    for (const run of runs) {
      // Skip runs that don't contain any real lower cabinet — these come
      // from solo blank-panel fillers at corner buffers, and a 25"-deep
      // countertop above them would jut into the perpendicular wall's
      // corner box and overlap an appliance (fridge / stove) on that
      // wall. The panel renders with a finished top without a counter.
      if (!run.parentIds || run.parentIds.length === 0) continue;
      const ct = buildCountertopForRun(wall, run, floorY, dims);
      if (!ct) continue;
      // Declared, as a seating overhang is: the slab reaches past its boxes to
      // the wall on purpose, and the pass that squares a counter back onto its
      // cabinets (`resyncCountertopsToCabinets`) must not take it off again.
      // World points, so they hold whichever way the slab's own axis runs.
      const scribeTo = (run.scribeParams || [])
        .map((p) => wallParamToSurfacePoint(wall, p))
        .filter(Boolean);
      countertops.push(scribeTo.length ? { ...ct, scribeTo } : ct);
    }
  }
  return countertops;
}

module.exports = { buildCountertopRuns, groupIntoRuns };
